import React, {
    KeyboardEventHandler,
    useState,
    ChangeEvent,
    FocusEvent,
} from "react";
import CreatableSelect from "react-select/creatable";
import { OnChangeValue } from "react-select";

const components = {
    DropdownIndicator: null,
};

export interface InternalOption {
    readonly label: string;
    readonly value: string;
}

const tagOption = (label: string): InternalOption => ({
    label,
    value: label,
});

interface CommonChosenInputProps {
    placeholder?: string;
    state: InternalOption[];
    setState?: any;
    isRequired?: boolean;
    contactFormType?: "default" | "association";
    isDisable?: boolean;
    isReadOnly?: boolean;
    setEditable?: ((editable: boolean) => void) | null;
    callBackFn?: ((value: OnChangeValue<InternalOption, true> | InternalOption) => void) | null;
}


const CommonChosenInput: React.FC<CommonChosenInputProps> = ({
    placeholder = "",
    state,
    setState,
    isRequired = false,
    contactFormType = "default",
    isDisable = false,
    isReadOnly = false,
    setEditable = null,
    callBackFn = null,
}) => {
    const [stateValue, setStateValue] = useState<string>("");

    const handleChange = (value: OnChangeValue<InternalOption, true>) => {
        if (callBackFn !== null) {
            callBackFn(value);
            return;
        }
        if (contactFormType === "association") {
            const primitiveValues = (value as InternalOption[]).map((v) => tagOption(v.value));
            setState?.(primitiveValues);

        } else {
            setState?.(value as InternalOption[]);
            setEditable?.(true);
        }
    };

    const handleInputChange = (inputValue: string) => {
        if (contactFormType === "association") return "";
        setStateValue(inputValue);
        setEditable?.(true);
        return inputValue;
    };

    const handleRequiredTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== "Enter") return;
        const value = (e.target as HTMLInputElement).value;
        if (value.trim()) {
            setState?.([...state, tagOption(value)]);
        }
        e.preventDefault();
    };

    const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value.trim()) {
            setState?.([...state, tagOption(value)]);
        }
    };

    const saveValue = () => {
        if (!stateValue) return;
        setState?.([...state, tagOption(stateValue)]);
        setStateValue("");
    };

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
        if (!stateValue) return;

        switch (event.key) {
            case "Enter":
            case "Tab": {
                if (callBackFn !== null) {
                    callBackFn(tagOption(stateValue));
                } else {
                    saveValue();
                }
                setStateValue("");
                event.preventDefault();
                break;
            }
        }

        setEditable?.(true);
    };


    return (
        <>
            {isRequired && state?.length === 0 ? (
                <input
                    className="rounded-md p-2 w-[100%] mt-2 border !border-text-1 text-sm"
                    type="text"
                    onKeyDown={handleRequiredTag}
                    onBlur={handleOnBlur}
                    placeholder={placeholder}
                    required
                    readOnly={isReadOnly}

                />
            ) : (
                <CreatableSelect
                    components={components}
                    inputValue={stateValue}
                    autoFocus={state?.length !== 0}
                    isClearable
                    isMulti
                    menuIsOpen={false}
                    onChange={handleChange}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onMenuClose={saveValue}
                    placeholder={placeholder}
                    value={state}
                    isDisabled={isDisable || isReadOnly}
                    className="bg-text-0"
                />
            )}
        </>
    );
};

export default CommonChosenInput;
